import {useCallback} from 'react';
import {useDispatch} from 'react-redux';
import moment from 'moment';
import AccountStore from 'src/stores/AccountStore';
import ActionsFactory from 'src/actions/ActionsFactory';
import {fetchAccountBalanceCreditCards} from 'src/redux/slices/accountSlice';
import FirebaseAnalytic from 'src/nativeModules/FirebaseAnalytic';
import CrashlyticsEvents from 'src/logging/Crashlytics';
import Events from 'src/logging/Events';
import {generateErrorMessage} from 'src/logging/generateErrorMessage';
import type {CreditCard} from 'src/models/CreditCard';
import type {AppDispatch} from 'src/redux/store';

interface UseCreditCardSyncProps {
  accountId: string;
  defaultBalanceId: string;
  creditCards: CreditCard[];
}

export const useCreditCardSync = ({
  accountId,
  defaultBalanceId,
  creditCards,
}: UseCreditCardSyncProps) => {
  const dispatch = useDispatch<AppDispatch>();

  const handleCreditCardUpdate = useCallback(
    async (params?: {isDefault?: boolean}) => {
      await dispatch(
        fetchAccountBalanceCreditCards({
          accountId,
          accountBalanceId: defaultBalanceId,
        }),
      );

      if (params?.isDefault && creditCards.length > 0) {
        const recentCard = creditCards.reduce((latest, current) =>
          moment(current.dateCreated).isAfter(moment(latest.dateCreated))
            ? current
            : latest,
        );

        try {
          await ActionsFactory.getAccountActions().updateDefaultToken(
            accountId,
            recentCard.accountBalanceId || '',
            recentCard.id || '',
          );

          FirebaseAnalytic.trackEvent(
            'onCreditCardAddedOrDeleted',
            'CreditCardScreen',
            {
              accountId,
              creditCardId: recentCard.id,
            },
          );
        } catch (error) {
          const errorMessage = generateErrorMessage(error);
          CrashlyticsEvents.log(
            'Exception',
            'CreditCardsScreen:OnCreditCardAddedOrDeleted',
            errorMessage,
          );
          Events.Error.trackEvent(
            'Exception',
            'CreditCardsScreen:OnCreditCardAddedOrDeleted',
            errorMessage,
          );
        }
      }
    },
    [accountId, defaultBalanceId, creditCards, dispatch],
  );

  const setupCreditCardListeners = useCallback(() => {
    AccountStore.addCreditCardAddedListener(handleCreditCardUpdate);
    AccountStore.addCreditCardDeletedListener(handleCreditCardUpdate);
    AccountStore.addDefaultTokenUpdatedListener(handleCreditCardUpdate);

    return () => {
      AccountStore.removeCreditCardAddedListener(handleCreditCardUpdate);
      AccountStore.removeCreditCardDeletedListener(handleCreditCardUpdate);
      AccountStore.removeDefaultTokenUpdatedListener(handleCreditCardUpdate);
    };
  }, [handleCreditCardUpdate]);

  return {setupCreditCardListeners};
};
